import React, { useState } from 'react';
import {
  Box,
  Grid,
  Tab,
  Tooltip,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogProps,
  Badge,
  makeStyles,
} from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { IRealEstateModel } from 'interfaces';
import { AccidentList, EventList, PersonalList, ReportList, ServiceCompanyList } from 'pages/PageRealEstate/views';
import { syncRooms } from 'pages/PageRealEstate/api';
import useError from 'components/ErrorProvider/useError';

const useStyles = makeStyles({
  badge: { animation: 'blinker 500ms linear infinite', top: -5, right: -12 },
});

interface IProps {
  realEstate: Partial<IRealEstateModel>;
  isAccidentsBadgeVisible: boolean;
  isEventsBadgeVisible: boolean;
  onSyncRooms: () => void;
  onClose: () => void;
}

const RealEstateInfo: React.FC<IProps & DialogProps> = ({
  open,
  realEstate,
  isAccidentsBadgeVisible,
  isEventsBadgeVisible,
  onSyncRooms,
  onClose,
}) => {
  // Local state
  const [tab, setTab] = useState('service-org');
  const [syncRoomsLoading, setSyncRoomsLoading] = useState(false);
  const [showSyncRoomsText, setShownSyncRoomsText] = useState(false);

  // Styles
  const classes = useStyles();

  // Global notification
  const { addError } = useError();

  // Methods
  const handleSyncRoomsInfo = (realEstateGuid: string) => {
    setSyncRoomsLoading(true);
    onSyncRooms();

    syncRooms(realEstateGuid)
      .then(() => setShownSyncRoomsText(true))
      .catch((error) => {
        addError(error.response.data.message);
      })
      .finally(() => {
        setTimeout(() => setShownSyncRoomsText(false), 5000);
        setSyncRoomsLoading(false);
      });
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string): void => {
    if (!realEstate || tab === newValue) return;

    setTab(newValue);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle disableTypography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography component="h2" variant="h5">
                {realEstate.name}
              </Typography>

              <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Обновить Помещения">
                  <IconButton disabled={syncRoomsLoading} onClick={() => handleSyncRoomsInfo(realEstate.guid!)}>
                    <Refresh />
                  </IconButton>
                </Tooltip>

                {showSyncRoomsText && <Typography color="textSecondary">Обновлено</Typography>}
              </Box>
            </Box>

            <Typography component="h3" color="textSecondary">
              {realEstate.address}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid item xs={12}>
          <TabContext value={tab}>
            <TabList onChange={handleChangeTab}>
              <Tab label="Сервисная компания" value="service-org" />
              <Tab label="Персонал" value="personal" />

              <Tab
                label={
                  isAccidentsBadgeVisible ? (
                    <Badge color="secondary" classes={{ badge: classes.badge }} badgeContent=" ">
                      Аварии
                    </Badge>
                  ) : (
                    'Аварии'
                  )
                }
                value="accidents"
              />

              <Tab
                label={
                  isEventsBadgeVisible ? (
                    <Badge color="secondary" classes={{ badge: classes.badge }} badgeContent=" ">
                      События
                    </Badge>
                  ) : (
                    'События'
                  )
                }
                value="events"
              />

              <Tab label="Отчеты" value="reports" />
            </TabList>

            <TabPanel value="service-org">
              <ServiceCompanyList realEstateId={realEstate.guid!} />
            </TabPanel>

            <TabPanel value="personal">
              <PersonalList realEstateId={realEstate.guid!} />
            </TabPanel>

            <TabPanel value="accidents">
              <AccidentList realEstateId={realEstate.guid!} />
            </TabPanel>

            <TabPanel value="events">
              <EventList realEstateId={realEstate.guid!} />
            </TabPanel>

            <TabPanel value="reports">
              <ReportList realEstateId={realEstate.guid!} />
            </TabPanel>
          </TabContext>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default RealEstateInfo;
